import React from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import CancelButton from '~/components/core/Buttons/CancelButton';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { ExportConfigurationRequest } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

interface CreateSnapshotDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateSnapshotDialog: React.FC<CreateSnapshotDialogProps> = ({ onClose, onSuccess }) => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();

  const handleSubmit = async (values: ExportConfigurationRequest) => {
    try {
      await axios.post(
        `/sysconfig/api/v1/configuration_repository/organizations/${organization.id}/snapshots/manual`,
        values
      );
      onSuccess();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  return (
    <Formik<ExportConfigurationRequest>
      initialValues={{
        snapshot_name: '',
        description: '',
      }}
      validationSchema={Yup.object().shape({
        snapshot_name: Yup.string().trim().required('Required').max(100, 'Must be 100 characters or less'),
        description: Yup.string().trim().max(500, 'Must be 500 characters or less'),
      })}
      onSubmit={async (values, formikProps) => {
        try {
          await handleSubmit(values);
        } catch (error) {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => (
        <CardDialog title="Create Configuration Snapshot" isDialog onClose={onClose} maxWidth="md">
          <div className="grid gap-20">
            {/* Form Fields */}
            <div className="grid gap-16">
              <TextFieldFormik id="snapshot_name" label="Snapshot Name" disabled={isSubmitting} />
              <TextFieldFormik id="description" label="Description" multiline rows={3} disabled={isSubmitting} />
            </div>

            {/* Progress Indicator */}
            {isSubmitting && (
              <div className="bg-blue-50 flex items-center justify-center gap-12 rounded p-16">
                <CircularProgress size={20} />
                <Text variant={Text.VARIANTS.SM}>Creating snapshot...</Text>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-32 flex w-full justify-end gap-12">
            <CancelButton disabled={isSubmitting} onClick={onClose} />
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
              Create Snapshot
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default CreateSnapshotDialog;
